<template>
	<CookieTemplateLayout
		:header-title="'Collection Templates' | useLabels('Collection Templates')"
		:header-caption="'Add, edit and manage collection templates' | useLabels('Add, edit and manage collection templates')"
	>
		<template #content>
			<FilterBar
				:search-query.sync="cookieCollectionTemplateSearchQuery"
				search-query-label="Collection Template Name"
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				@persistSearchQuery="changeCookieCollectionTemplateSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
			>
				<template #action>
					<PrimaryActionButton
						v-if="userCanCreateUpdate"
						@click="onCreateClick()"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Create
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<SectionCard>
				<template #title>
					Collection Templates
				</template>
				<template #body>
					<DataTable
						:headers="tableHeaders"
						:items="filteredCookieCollectionTemplates"
						sort-by="id"
						:sort-desc="true"
						@click:row="onEditClick"
					>
						<template #item.action="{ item }">
							<v-menu
								offset-y
								left
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										small
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="!item.isSystem && userCanCreateUpdate && userHasBrand(item.brandId)"
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Edit
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-else
										@click="onEditClick(item)"
									>
										<v-list-item-content>
											<v-list-item-title>
												View
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanCreateUpdate && userHasBrand(item.brandId)"
										@click.stop.prevent="onCloneClick(item, true)"
									>
										<v-list-item-content>
											<v-list-item-title>
												Clone
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
									<v-list-item
										v-if="userCanDelete && item.usage === 0 && !item.isSystem && userHasBrand(item.brandId)"
										@click.stop.prevent="cookieCollectionTemplateToDelete = item"
									>
										<v-list-item-content>
											<v-list-item-title>
												Delete
											</v-list-item-title>
										</v-list-item-content>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</template>
			</SectionCard>
			<ConfirmDeleteModal
				v-if="cookieCollectionTemplateToDelete && userCanDelete"
				:entity-name="cookieCollectionTemplateToDelete.name"
				entity-type="Collection Template"
				@close="cookieCollectionTemplateToDelete = null"
				@delete="deleteCookieCollectionTemplate"
			/>
		</template>
	</CookieTemplateLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import CookieTemplateLayout from '../cookie-template-layout.vue'
import FilterBar from '../../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import SectionCard from '../../../../../../../shared/components/section-card.vue'
import ConfirmDeleteModal from '../../../../../../../shared/components/confirm-delete-modal.vue'
import DataTable from '../../../../../../../shared/components/data-table.vue'
import { useLabels } from '../../../../../../../shared/state/admin-portal-navigation.js'
import { MANAGE_COOKIE_COLLECTION_TEMPLATE } from '../../../../../router/route-names.js'
import { getCookieCollectionTemplates, deleteCookieCollectionTemplate } from '../../../../../../../shared/utils/api/cookies/cookie-collection-templates.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { CAN_CREATE_UPDATE_COOKIE_COLLECTION_TEMPLATES, CAN_DELETE_COOKIE_COLLECTION_TEMPLATES } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import { changeCookieCollectionTemplateSearchQuery, cookieCollectionTemplateSearchQuery } from '../../../../../../../shared/state/cookie-collection-templates.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasBrand } from '../../../../../../../shared/state/brands.js'
export default {
	components: {
		CookieTemplateLayout,
		FilterBar,
		PrimaryActionButton,
		SectionCard,
		ConfirmDeleteModal,
		DataTable
	},
	setup () {
		return {
			userHasBrand,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			changeCookieCollectionTemplateSearchQuery,
			cookieCollectionTemplateSearchQuery
		}
	},
	data () {
		return {
			cookieCollectionTemplates: [],
			cookieCollectionTemplateToDelete: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanCreateUpdate () {
			return this.productAreaPermission(CAN_CREATE_UPDATE_COOKIE_COLLECTION_TEMPLATES)
		},
		userCanDelete () {
			return this.productAreaPermission(CAN_DELETE_COOKIE_COLLECTION_TEMPLATES)
		},
		tableHeaders () {
			return [
				{ value: 'id', text: 'ID', width: '6%' },
				{ value: 'brandName', text: useLabels('Brand'), width: '16%' },
				{ value: 'name', text: 'Collection Template Name', width: '44%' },
				{ value: 'usage', text: 'Usage', width: '12%' },
				{ value: 'createdDate', text: 'Created Date', width: '22%' },
				{ value: 'action', text: 'Action', width: '80px', align: 'center', sortable: false }
			]
		},
		filteredCookieCollectionTemplates () {
			return this.cookieCollectionTemplates.filter(({ name, brandId }) => {
				let check = true
				if (cookieCollectionTemplateSearchQuery.value) check = name.toLowerCase().includes(cookieCollectionTemplateSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandId === selectedAdminPortalBrandId.value
				return check
			}).map(collectionTemplate => ({
				...collectionTemplate
			}))
		}
	},
	created () {
		this.getCookieCollectionTemplates()
	},
	methods: {
		onCreateClick () {
			this.$router.push({
				name: MANAGE_COOKIE_COLLECTION_TEMPLATE
			})
		},
		onEditClick (item) {
			this.$router.push({
				name: MANAGE_COOKIE_COLLECTION_TEMPLATE,
				params: {
					cookieCollectionTemplateToEdit: item
				}
			})
		},
		onCloneClick (item, cloneMode) {
			const cookieCollectionTemplateToEdit = JSON.parse(JSON.stringify(item))
			cookieCollectionTemplateToEdit.name = cookieCollectionTemplateToEdit.name + ' (clone)'
			this.$router.push({
				name: MANAGE_COOKIE_COLLECTION_TEMPLATE,
				params: {
					cookieCollectionTemplateToEdit,
					cloneMode: cloneMode
				}
			})
		},
		async getCookieCollectionTemplates () {
			const { cookieCollectionTemplates } = await getCookieCollectionTemplates()
			this.cookieCollectionTemplates = cookieCollectionTemplates
		},
		async deleteCookieCollectionTemplate () {
			await deleteCookieCollectionTemplate(this.cookieCollectionTemplateToDelete.id)
			showSnackbar('You have removed this collection template')
			this.cookieCollectionTemplateToDelete = null
			this.getCookieCollectionTemplates()
		}
	}
}

</script>
